import { useState, useEffect } from "react";
import { Box, IconButton, CircularProgress } from "@mui/material";
import { MoreVert as MoreVertIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import ActionsMenu from "../../components/ActionsMenu";
import DeleteConfirmationDialog from "../../components/DeleteDialog";
import DataTable from "../../components/DataTable";
import axios from "axios";
import { useTranslation } from "react-i18next";

const roleConfigs = [
  { role: "employee", titleKey: "employees_list" },
  { role: "supplier", titleKey: "suppliers_list" },
  { role: "customer", titleKey: "customers_list" },
];


const RoleBasedTables = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [employeesData, setEmployeesData] = useState({});
  const [loading, setLoading] = useState(true);
  const apiUrl = "https://oven-backend.onrender.com";


  useEffect(() => {
    const fetchEmployeesByRole = async () => {
      setLoading(true);
      try {
        const data = {};
        for (const config of roleConfigs) {
          const response = await axios.get(`${apiUrl}/employees/role/${config.role}`);
          data[config.role] = response.data;
        }
        setEmployeesData(data);
      } catch (error) {
        console.error("Error fetching employees:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeesByRole();
  }, []);

  const handleMenuOpen = (event, employeeId) => {
    setAnchorEl(event.currentTarget);
    setSelectedEmployeeId(employeeId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditEmployee = () => {
    navigate(`/UpdateEmployee/${selectedEmployeeId}`);
    handleMenuClose();
  };

  const handleDeleteDialogOpen = () => {
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${apiUrl}/employees/${selectedEmployeeId}`);
      setEmployeesData((prevData) => {
        const updatedData = { ...prevData };
        for (const role in updatedData) {
          updatedData[role] = updatedData[role].filter(emp => emp._id !== selectedEmployeeId);
        }
        return updatedData;
      });
    } catch (error) {
      console.error("Error deleting employee:", error);
    } finally {
      handleDeleteDialogClose();
    }
  };


    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === "ar";
  
    const commonColumns = [
      { field: "name", headerName: t("name"), flex: 1 },
      { field: "phone", headerName: t("phone"), flex: 1 },
    ];
  
    const actionsColumn = {
      field: "actions",
      headerName: t("actions"),
      flex: 0.5,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={(event) => handleMenuOpen(event, params.row._id)}>
            <MoreVertIcon />
          </IconButton>
          <ActionsMenu 
            anchorEl={anchorEl}
            handleMenuClose={handleMenuClose}
            menuItems={[
              { label: t("edit"), action: handleEditEmployee, icon: <EditIcon /> },
              { label: t("delete"), action: handleDeleteDialogOpen, icon: <DeleteIcon />, style: { color: "red" } }
            ]}
          />
        </Box>
      ),
    };
  
    const roleSpecificColumns = {
      employee: [
        { field: "salary", headerName: t("salary"), flex: 1 },
        { 
          field: "hire_date", 
          headerName: t("hire_date"), 
          flex: 1, 
          valueGetter: ({ value }) => value ? new Date(value).toISOString().split('T')[0] : t("not_available") 
        },
      ],
      supplier: [
        { field: "companyName", headerName: t("company_name"), flex: 1 },
      ],
      customer: [
        { field: "customerCode", headerName: t("customer_code"), flex: 1 },
      ],
    };

    return (
      <Box m="20px" sx={{ direction: isRTL ? "rtl" : "ltr" }}>
        {loading ? (
          <CircularProgress />
        ) : (
          roleConfigs.map(({ role, titleKey  }) => (
            <DataTable
              key={role}
              title={t(titleKey )}
              columns={[...commonColumns, ...roleSpecificColumns[role], actionsColumn]}
              rows={employeesData[role] || []}
              getRowId={(row) => row._id}
            />
          ))
        )}
  
        <DeleteConfirmationDialog
          open={openDeleteDialog}
          onClose={handleDeleteDialogClose}
          onConfirm={handleConfirmDelete}
          title={t("delete_employee")}
          message={t("delete_employee_message")}
          confirmColor="error"
          cancelColor="secondary"
          size="large"
        />
      </Box>
    );
};

export default RoleBasedTables;
